import { useTranslation } from "react-i18next";

import Label from "../../../../shared/ui/form/Label";
import Input from "../../../../shared/ui/form/input/InputField";
import type { PatientCustomField } from "../../domain/PatientCustomField";

interface Props {
    field: PatientCustomField;
    id: string;
    value: string;
    onChange: (value: string) => void;
    /** i18n key of what is wrong with the value, if anything. */
    error?: string;
}

const SELECT_CLASS = "field-base field-default h-11 w-full px-4 text-sm";

/**
 * One of a centre's own fields, as its type asks to be filled in: a list of
 * its options, or an input for text, a number or a date. Labelled, marked
 * when required, and pointing at its error.
 */
export default function CustomFieldInput({ field, id, value, onChange, error }: Props) {
    const { t } = useTranslation();
    const errorId = `${id}-error`;

    return (
        <div>
            <Label htmlFor={id}>
                {field.label}
                {field.required && (
                    <span className="text-status-critical" aria-hidden="true">
                        {" "}
                        *
                    </span>
                )}
            </Label>
            {field.field_type === "select" ? (
                <select
                    id={id}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    aria-invalid={Boolean(error)}
                    aria-describedby={error ? errorId : undefined}
                    aria-required={field.required}
                    className={SELECT_CLASS}
                >
                    <option value="">{t("center.patientFields.card.noValue")}</option>
                    {value && !field.options.includes(value) && <option value={value}>{value}</option>}
                    {field.options.map((option) => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </select>
            ) : (
                <Input
                    id={id}
                    type={field.field_type === "text" ? "text" : field.field_type}
                    inputMode={field.field_type === "number" ? "decimal" : undefined}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    required={field.required}
                    error={Boolean(error)}
                    aria-invalid={Boolean(error)}
                    aria-describedby={error ? errorId : undefined}
                />
            )}
            {error && (
                <p id={errorId} className="mt-1 text-xs text-status-critical">
                    {t(error)}
                </p>
            )}
        </div>
    );
}
